import React, { useState, useEffect } from 'react';
import { LMSProvider } from '../../types/lms';
import { Database, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { LMSService } from '../../services/LMSService';
import { useSchool } from '../../contexts/SchoolContext';
import AppLayout from '../layout/AppLayout';
import LMSSyncStatus from '../lms/LMSSyncStatus';
import toast from 'react-hot-toast';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function LMSSettingsPage() {
  useDocumentTitle('LMS Settings');
  
  const { school } = useSchool();
  const [provider, setProvider] = useState<LMSProvider>({
    id: '',
    name: '',
    type: 'canvas',
    syncEnabled: false,
    syncInterval: 300, // 5 minutes
    syncSettings: {
      attendance: true,
      grades: false,
      discipline: false,
      schedules: false
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      if (!school?.id) return;
      
      try {
        setLoading(false);
        // Load existing LMS provider if available
        // For now, we'll use default settings
      } catch (error) {
        console.error('Error loading LMS settings:', error);
        toast.error('Failed to load LMS settings');
        setLoading(false);
      }
    };

    loadSettings();
  }, [school?.id]);

  const handleSave = async () => {
    if (!school?.id) return;

    try {
      setSaving(true);
      await LMSService.configureLMSProvider(school.id, provider);
      toast.success('LMS settings saved successfully');
    } catch (error) {
      console.error('Error saving LMS settings:', error);
      toast.error('Failed to save LMS settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);
      const isConnected = await LMSService.testLMSConnection(provider);
      setConnectionStatus(isConnected ? 'success' : 'error');
      toast.success(isConnected ? 'LMS connection successful' : 'LMS connection failed');
    } catch (error) {
      console.error('Error testing LMS connection:', error);
      setConnectionStatus('error');
      toast.error('Failed to connect to LMS');
    } finally {
      setTestingConnection(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading LMS settings...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Database className="w-8 h-8 text-primary-500" />
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">LMS Integration</h1>
              <p className="text-text-secondary">Configure your Learning Management System connection</p>
            </div>
          </div>

          <div className="bg-surface-light rounded-lg p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                LMS Provider
              </label>
              <select
                value={provider.type}
                onChange={(e) => setProvider({ ...provider, type: e.target.value as LMSProvider['type'] })}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              >
                <option value="canvas">Canvas</option>
                <option value="blackboard">Blackboard</option>
                <option value="powerschool">PowerSchool</option>
                <option value="infinite-campus">Infinite Campus</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                API URL
              </label>
              <input
                type="url"
                value={provider.apiUrl || ''}
                onChange={(e) => setProvider({ ...provider, apiUrl: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="https://your-lms.edu/api/v1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                API Key
              </label>
              <input
                type="password"
                value={provider.apiKey || ''}
                onChange={(e) => setProvider({ ...provider, apiKey: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                         focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                placeholder="••••••••••••••••"
              />
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={testConnection}
                disabled={testingConnection || !provider.apiUrl || !provider.apiKey}
                className="px-4 py-2 bg-background text-text-primary rounded-lg hover:bg-primary-50 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed 
                         flex items-center gap-2"
              >
                {testingConnection ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : connectionStatus === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-success-DEFAULT" />
                ) : connectionStatus === 'error' ? (
                  <XCircle className="w-5 h-5 text-error-DEFAULT" />
                ) : (
                  <Database className="w-5 h-5" />
                )}
                Test Connection
              </button>

              {connectionStatus === 'success' && (
                <span className="text-sm text-success-DEFAULT">Connection successful</span>
              )}
              {connectionStatus === 'error' && (
                <span className="text-sm text-error-DEFAULT">Connection failed</span>
              )}
            </div>

            <div className="pt-4 border-t border-border-light">
              <h3 className="text-lg font-medium text-text-primary mb-4">Sync Settings</h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-text-primary">Enable Automatic Sync</h4>
                    <p className="text-sm text-text-secondary">
                      Automatically sync data between systems
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={provider.syncEnabled}
                      onChange={(e) => setProvider({ ...provider, syncEnabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-border-light peer-focus:outline-none rounded-full peer 
                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500">
                    </div>
                  </label>
                </div>

                {provider.syncEnabled && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-1">
                        Sync Interval (minutes)
                      </label>
                      <input
                        type="number"
                        min="5"
                        max="1440"
                        value={provider.syncInterval / 60}
                        onChange={(e) => setProvider({ ...provider, syncInterval: parseInt(e.target.value) * 60 })}
                        className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                                 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-secondary mb-2">
                        Sync Settings
                      </label>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="attendance"
                            checked={provider.syncSettings.attendance}
                            onChange={(e) => setProvider({
                              ...provider,
                              syncSettings: { ...provider.syncSettings, attendance: e.target.checked }
                            })}
                            className="mr-2"
                          />
                          <label htmlFor="attendance" className="text-sm text-text-primary">
                            Attendance Data
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="grades"
                            checked={provider.syncSettings.grades}
                            onChange={(e) => setProvider({
                              ...provider,
                              syncSettings: { ...provider.syncSettings, grades: e.target.checked }
                            })}
                            className="mr-2"
                          />
                          <label htmlFor="grades" className="text-sm text-text-primary">
                            Grade Data
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="discipline"
                            checked={provider.syncSettings.discipline}
                            onChange={(e) => setProvider({
                              ...provider,
                              syncSettings: { ...provider.syncSettings, discipline: e.target.checked }
                            })}
                            className="mr-2"
                          />
                          <label htmlFor="discipline" className="text-sm text-text-primary">
                            Discipline Records
                          </label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="schedules"
                            checked={provider.syncSettings.schedules}
                            onChange={(e) => setProvider({
                              ...provider,
                              syncSettings: { ...provider.syncSettings, schedules: e.target.checked }
                            })}
                            className="mr-2"
                          />
                          <label htmlFor="schedules" className="text-sm text-text-primary">
                            Schedule Data
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

          {/* LMS Sync Status */}
          <div className="mt-6">
            <LMSSyncStatus />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}